import classNames from "classnames"
import React, { useEffect, useMemo, useRef } from "react"

import styles from "./index.module.scss"

export type StrokeLayer = {
    color: string
    width: number // visual thickness outside字形边缘的宽度（像素）
}

export type FillPattern = {
    color1: string
    color2: string
    width: number
}

export interface StrokeTextProps {
    text: string
    // 单层外描边（如果提供 outer 优先于这两个顶层字段）
    borderWidth?: number
    borderColor?: string
    // 双层描边
    inner?: StrokeLayer // 贴近字形的内圈描边
    outer?: StrokeLayer // 最外圈描边
    // 文本与画布
    fontSize?: number // px
    fontFamily?: string
    fontWeight?: number | string
    fillColor?: string
    fillPattern?: FillPattern
    lineHeight?: number // 绝对行高（px）
    lineHeightRatio?: number // 行高倍率，随字号自动缩放
    padding?: number // 画布内边距（px）
    offsetY?: number // 描边 Y 轴偏移量
    maxWidth?: number // 最大文本宽度（px），超出时自动缩字号并换行
    maxHeight?: number // 最大文本高度（px），超出时自动缩字号并裁剪行数
    maxFontSize?: number // 自动缩放时允许的最大字号（px）
    minFontSize?: number // 自动缩放时允许的最小字号（px）
    maxLines?: number // 最大行数，超出时裁剪到最后一行并追加省略号
    textAlign?: "left" | "center" | "right"
    /** 首行按 layout 基线对齐：基线距内容区顶为 layout.ascent，便于多段文案横向基线对齐 */
    baselineAligned?: boolean
    /** 多行时收窄宽度使各行长度趋于均匀（类似 CSS text-wrap: balance） */
    balanceLines?: boolean
    dpr?: number // 自定义设备像素比，默认使用 window.devicePixelRatio
    className?: string
    style?: React.CSSProperties
}

type LayoutLine = {
    text: string
    width: number
    ascent: number
    descent: number
}

type TextLayout = {
    lines: LayoutLine[]
    fontSize: number
    textWidth: number
    textHeight: number
    ascent: number
    descent: number
    lineHeight: number
    wrappedLineCount: number
    wrappedTextHeight: number
}

const fontLoadCache = new Map<string, Promise<void>>()

const waitForFont = (font: string, text: string) => {
    if (typeof document === "undefined" || !("fonts" in document)) {
        return Promise.resolve()
    }

    const fontFaceSet = document.fonts
    if (fontFaceSet.check(font, text)) {
        return Promise.resolve()
    }

    const cacheKey = `${font}__${text}`
    const cachedPromise = fontLoadCache.get(cacheKey)
    if (cachedPromise) {
        return cachedPromise
    }

    const pendingPromise = (async () => {
        try {
            await fontFaceSet.load(font, text)
        } catch {
            // Fall through to the current browser font state.
        }

        if (!fontFaceSet.check(font, text)) {
            try {
                await fontFaceSet.ready
            } catch {
                // Ignore readiness failures and continue with the current font state.
            }
        }
    })().finally(() => {
        fontLoadCache.delete(cacheKey)
    })

    fontLoadCache.set(cacheKey, pendingPromise)
    return pendingPromise
}

// ---------------------------------------------------------------------------
// 分词：优先 Intl.Segmenter（词级），不支持时逐 CJK 字符拆分
// ---------------------------------------------------------------------------

const CJK_RE =
    /[\u3000-\u303f\u3040-\u309f\u30a0-\u30ff\u4e00-\u9fff\uf900-\ufaff\uff00-\uffef]/

let _segmenter: Intl.Segmenter | null | undefined

const getSegmenter = (): Intl.Segmenter | null => {
    if (_segmenter !== undefined) return _segmenter
    try {
        _segmenter = new Intl.Segmenter("zh-CN", { granularity: "word" })
    } catch {
        _segmenter = null
    }
    return _segmenter
}

const splitCJKChars = (text: string): string[] => {
    const result: string[] = []
    let buf = ""
    for (const ch of text) {
        if (CJK_RE.test(ch)) {
            if (buf) {
                result.push(buf)
                buf = ""
            }
            result.push(ch)
        } else {
            buf += ch
        }
    }
    if (buf) result.push(buf)
    return result
}

// 禁止出现在行首的字符（标点、助词、方位词等），合并到前一个 token
const NO_START_RE =
    /^[，。！？；：、）》」』"'…—～·\u3002\uff01\uff1f.,!?;:)\]}>的地得了着过吗呢吧啊啦嘛呀哦哇里中上下]$/

// 禁止出现在行尾的字符（开括号类），合并到后一个 token
const NO_END_RE = /^[（《「『"'([{<]$/

const mergeBreakTokens = (tokens: string[]): string[] => {
    if (tokens.length <= 1) return tokens

    const merged: string[] = []
    let pending = ""

    for (const token of tokens) {
        if (pending) {
            pending += token
            merged.push(pending)
            pending = ""
            continue
        }

        if (NO_END_RE.test(token)) {
            pending = token
            continue
        }

        if (
            merged.length > 0 &&
            (NO_START_RE.test(token) || NO_START_RE.test(token[0]))
        ) {
            merged[merged.length - 1] += token
            continue
        }

        merged.push(token)
    }

    if (pending) {
        if (merged.length > 0) {
            merged[merged.length - 1] += pending
        } else {
            merged.push(pending)
        }
    }

    return merged
}

const tokenizeText = (value: string): string[] => {
    const segmenter = getSegmenter()
    let tokens: string[]

    if (segmenter) {
        tokens = [...segmenter.segment(value)].map((s) => s.segment)
    } else {
        const baseTokens = value.match(/\s+|\S+/g) ?? [value]
        tokens = baseTokens.flatMap((token) => {
            if (/^\s+$/u.test(token)) return [token]

            if (CJK_RE.test(token)) return splitCJKChars(token)

            if (!token.includes("-")) return [token]
            const parts = token.split("-")
            return parts.flatMap((part, index) => {
                if (index === parts.length - 1) return part ? [part] : []
                const segment = `${part}-`
                return [segment || "-"]
            })
        })
    }

    return mergeBreakTokens(tokens)
}

/**
 * 通过 Canvas 绘制“圆角外描边文本”
 * - 采用 lineJoin='round' 实现圆角
 * - 支持 outer/inner 双层描边：
 *    1) 先以 (outer.width + inner.width)*2 的线宽绘制 outer.color
 *    2) 再以 inner.width*2 的线宽绘制 inner.color
 *    3) 最后填充文本，得到内圈/外圈两种颜色的环形描边
 * - 若仅需要单层外描边：仅绘制 lineWidth=borderWidth*2 的描边再填充
 */
const StrokeText: React.FC<StrokeTextProps> = ({
    text,
    borderWidth,
    borderColor,
    inner,
    outer,
    fontSize = 48,
    fontFamily,
    fontWeight = "normal",
    fillColor = "#ffffff",
    fillPattern,
    lineHeight,
    lineHeightRatio,
    padding = 8,
    offsetY = 0,
    maxWidth,
    maxHeight,
    maxFontSize,
    minFontSize,
    maxLines,
    textAlign = "center",
    baselineAligned = false,
    balanceLines = false,
    dpr,
    className,
    style,
}) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null)
    const wrapperRef = useRef<HTMLDivElement | null>(null)

    const effectiveOuter: StrokeLayer | undefined = useMemo(() => {
        if (outer && outer.width > 0) return outer
        if (borderWidth && borderWidth > 0) {
            return {
                width: borderWidth,
                color: borderColor ?? "#000000",
            }
        }
        return undefined
    }, [outer, borderWidth, borderColor])

    const effectiveInner: StrokeLayer | undefined = useMemo(() => {
        if (inner && inner.width > 0) return inner
        return undefined
    }, [inner])

    const fontWeightValue = useMemo(() => {
        if (typeof fontWeight === "number" || typeof fontWeight === "string") {
            return String(fontWeight)
        }
        return "normal"
    }, [fontWeight])

    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas || !text) return

        let cancelled = false

        const draw = async () => {
            const ratio = Math.max(
                1,
                Math.floor(
                    dpr ||
                        (typeof window !== "undefined"
                            ? window.devicePixelRatio || 1
                            : 1),
                ),
            )
            const ctx = canvas.getContext("2d")
            if (!ctx) return

            const getFontString = (size: number) =>
                `${fontWeightValue} ${Math.round(size)}px ${fontFamily}`

            const resolvedMaxFontSize = Math.max(
                1,
                Math.round(maxFontSize ?? fontSize),
            )

            await waitForFont(getFontString(resolvedMaxFontSize), text)
            if (cancelled) return

            const getLineMetrics = (size: number) => {
                const metrics = ctx.measureText("Hg")
                const ascent =
                    "actualBoundingBoxAscent" in metrics
                        ? (metrics as TextMetrics).actualBoundingBoxAscent
                        : size * 0.8
                const descent =
                    "actualBoundingBoxDescent" in metrics
                        ? (metrics as TextMetrics).actualBoundingBoxDescent
                        : size * 0.2
                const naturalLineHeight = ascent + descent
                const configuredLineHeight =
                    typeof lineHeight === "number" &&
                    Number.isFinite(lineHeight) &&
                    lineHeight > 0
                        ? lineHeight
                        : typeof lineHeightRatio === "number" &&
                            Number.isFinite(lineHeightRatio) &&
                            lineHeightRatio > 0
                          ? size * lineHeightRatio
                          : size * 1.2
                return {
                    ascent,
                    descent,
                    lineHeight: Math.max(
                        naturalLineHeight,
                        configuredLineHeight,
                    ),
                }
            }

            const measureLine = (line: string): LayoutLine => {
                const metrics = ctx.measureText(line || " ")
                const ascent =
                    "actualBoundingBoxAscent" in metrics
                        ? (metrics as TextMetrics).actualBoundingBoxAscent
                        : fontSize * 0.8
                const descent =
                    "actualBoundingBoxDescent" in metrics
                        ? (metrics as TextMetrics).actualBoundingBoxDescent
                        : fontSize * 0.2

                return {
                    text: line,
                    width: ctx.measureText(line).width,
                    ascent,
                    descent,
                }
            }

            const measureLineWidth = (line: string) => measureLine(line).width
            const resolvedWidthLimit =
                maxWidth && maxWidth > 0 ? maxWidth : undefined
            const resolvedHeightLimit =
                maxHeight && maxHeight > 0 ? maxHeight : undefined
            const resolvedLineLimit =
                maxLines && maxLines > 0 ? maxLines : undefined
            const shouldAutoFit = Boolean(
                resolvedWidthLimit || resolvedHeightLimit || resolvedLineLimit,
            )

            const greedyWrap = (
                paragraph: string,
                widthLimit: number,
                allowWordBreak = false,
            ): LayoutLine[] => {
                const wrappedLines: LayoutLine[] = []
                const tokens = tokenizeText(paragraph)
                let currentLine = ""

                const pushLine = (line: string) => {
                    const normalizedLine = line.trimEnd()
                    wrappedLines.push(measureLine(normalizedLine))
                }

                const splitLongToken = (token: string) => {
                    let currentTokenLine = ""

                    Array.from(token).forEach((char) => {
                        const nextTokenLine = currentTokenLine + char
                        if (
                            currentTokenLine &&
                            measureLineWidth(nextTokenLine) > widthLimit
                        ) {
                            pushLine(currentTokenLine)
                            currentTokenLine = /^\s$/u.test(char) ? "" : char
                            return
                        }
                        currentTokenLine = nextTokenLine
                    })

                    currentLine = currentTokenLine.trimStart()
                }

                tokens.forEach((token) => {
                    if (!currentLine && /^\s+$/u.test(token)) {
                        return
                    }

                    const nextLine = currentLine + token
                    if (!currentLine) {
                        if (
                            measureLineWidth(token) <= widthLimit ||
                            !allowWordBreak
                        ) {
                            currentLine = token
                            return
                        }

                        splitLongToken(token)
                        return
                    }

                    if (measureLineWidth(nextLine) <= widthLimit) {
                        currentLine = nextLine
                        return
                    }

                    pushLine(currentLine)
                    currentLine = ""

                    if (/^\s+$/u.test(token)) {
                        return
                    }

                    if (measureLineWidth(token) <= widthLimit) {
                        currentLine = token.trimStart()
                        return
                    }

                    if (allowWordBreak) {
                        splitLongToken(token)
                        return
                    }

                    currentLine = token.trimStart()
                })

                if (currentLine || wrappedLines.length === 0) {
                    pushLine(currentLine)
                }

                return wrappedLines
            }

            const PUNCT_END_RE = /[，。！？；：、）》」』"'…—～·.,!?;:)\]}>]$/

            const bestTwoLineSplit = (
                tokens: string[],
                widthLimit: number,
            ): LayoutLine[] | null => {
                let bestResult: LayoutLine[] | null = null
                let bestScore = -1

                for (let i = 1; i < tokens.length; i++) {
                    const l1 = tokens.slice(0, i).join("").trimEnd()
                    const l2 = tokens.slice(i).join("").trimStart().trimEnd()
                    if (!l1 || !l2) continue

                    const w1 = measureLineWidth(l1)
                    const w2 = measureLineWidth(l2)
                    if (w1 > widthLimit || w2 > widthLimit) continue

                    const isPunct = PUNCT_END_RE.test(l1)
                    const balance = 1 - Math.abs(w1 - w2) / (widthLimit || 1)
                    const score = (isPunct ? 1000 : 0) + balance * 100

                    if (score > bestScore) {
                        bestScore = score
                        bestResult = [measureLine(l1), measureLine(l2)]
                    }
                }

                return bestResult
            }

            const wrapParagraph = (
                paragraph: string,
                widthLimit?: number,
                allowWordBreak = false,
            ): LayoutLine[] => {
                if (!paragraph) {
                    return [measureLine("")]
                }
                if (!widthLimit || widthLimit <= 0) {
                    return [measureLine(paragraph)]
                }

                const lines = greedyWrap(paragraph, widthLimit, allowWordBreak)

                if (!balanceLines || lines.length <= 1) {
                    return lines
                }

                if (lines.length === 2) {
                    const tokens = tokenizeText(paragraph)
                    const optimal = bestTwoLineSplit(tokens, widthLimit)
                    return optimal ?? lines
                }

                const targetCount = lines.length
                let lo = 0
                let hi = widthLimit

                while (hi - lo > 1) {
                    const mid = Math.floor((lo + hi) / 2)
                    const test = greedyWrap(paragraph, mid, allowWordBreak)
                    if (test.length <= targetCount) {
                        hi = mid
                    } else {
                        lo = mid
                    }
                }

                const balanced = greedyWrap(paragraph, hi, allowWordBreak)
                return balanced.length <= targetCount ? balanced : lines
            }

            const getHeightLineLimit = (
                nextLineHeight: number,
                heightLimit?: number,
            ) => {
                if (!heightLimit || heightLimit <= 0) {
                    return undefined
                }

                return Math.max(1, Math.floor(heightLimit / nextLineHeight))
            }

            const truncateLines = (
                lines: LayoutLine[],
                widthLimit: number | undefined,
                lineLimit: number,
            ): LayoutLine[] => {
                if (lines.length <= lineLimit) {
                    return lines
                }

                const visibleLines = lines.slice(0, lineLimit)
                const lastLine = visibleLines[lineLimit - 1]
                const ellipsis = "..."
                let truncatedText = lastLine.text
                const canMeasure = widthLimit && widthLimit > 0

                while (
                    truncatedText &&
                    canMeasure &&
                    measureLineWidth(`${truncatedText}${ellipsis}`) > widthLimit
                ) {
                    truncatedText = Array.from(truncatedText)
                        .slice(0, -1)
                        .join("")
                }

                visibleLines[lineLimit - 1] = {
                    ...measureLine(`${truncatedText}${ellipsis}`),
                }

                return visibleLines
            }

            const buildLayout = (
                size: number,
                allowWordBreak = false,
            ): TextLayout => {
                ctx.font = getFontString(size)
                const metrics = getLineMetrics(size)
                const paragraphs = text.split(/\r?\n/)
                const wrappedLines = paragraphs.flatMap((paragraph) =>
                    wrapParagraph(
                        paragraph,
                        resolvedWidthLimit,
                        allowWordBreak,
                    ),
                )
                const wrappedLineCount = Math.max(wrappedLines.length, 1)
                const wrappedTextHeight = wrappedLineCount * metrics.lineHeight
                const heightLineLimit = getHeightLineLimit(
                    metrics.lineHeight,
                    resolvedHeightLimit,
                )
                const combinedLineLimit = [
                    resolvedLineLimit,
                    heightLineLimit,
                ].reduce<number | undefined>((currentMin, value) => {
                    if (!value || value <= 0) {
                        return currentMin
                    }
                    if (!currentMin) {
                        return value
                    }
                    return Math.min(currentMin, value)
                }, undefined)
                const limitedLines = combinedLineLimit
                    ? truncateLines(
                          wrappedLines,
                          resolvedWidthLimit,
                          combinedLineLimit,
                      )
                    : wrappedLines
                const textWidth = limitedLines.reduce(
                    (maxLineWidth, line) => Math.max(maxLineWidth, line.width),
                    0,
                )
                const lineCount = Math.max(limitedLines.length, 1)
                const textHeight = lineCount * metrics.lineHeight

                return {
                    lines: limitedLines,
                    fontSize: size,
                    textWidth,
                    textHeight,
                    ascent: metrics.ascent,
                    descent: metrics.descent,
                    lineHeight: metrics.lineHeight,
                    wrappedLineCount,
                    wrappedTextHeight,
                }
            }

            const resolvedMinFontSize = Math.min(
                resolvedMaxFontSize,
                Math.max(
                    1,
                    Math.round(
                        minFontSize ?? Math.min(12, resolvedMaxFontSize),
                    ),
                ),
            )
            const hardMinFontSize = 1

            const doesLayoutFit = (candidateLayout: TextLayout) => {
                const fitsWidth =
                    !resolvedWidthLimit ||
                    candidateLayout.textWidth <= resolvedWidthLimit
                const fitsLineCount =
                    !resolvedLineLimit ||
                    candidateLayout.wrappedLineCount <= resolvedLineLimit
                const fitsHeight =
                    !resolvedHeightLimit ||
                    candidateLayout.wrappedTextHeight <= resolvedHeightLimit

                return fitsWidth && fitsLineCount && fitsHeight
            }

            let layout = buildLayout(resolvedMaxFontSize)
            if (shouldAutoFit) {
                const findFittedLayout = (from: number, to: number) => {
                    for (
                        let nextFontSize = from;
                        nextFontSize >= to;
                        nextFontSize -= 1
                    ) {
                        const nextLayout = buildLayout(nextFontSize)
                        layout = nextLayout
                        if (doesLayoutFit(nextLayout)) {
                            return true
                        }
                    }

                    return false
                }

                const fittedWithinSoftMin = findFittedLayout(
                    resolvedMaxFontSize,
                    resolvedMinFontSize,
                )
                if (
                    !fittedWithinSoftMin &&
                    resolvedMinFontSize > hardMinFontSize
                ) {
                    findFittedLayout(resolvedMinFontSize - 1, hardMinFontSize)
                }

                if (!doesLayoutFit(layout) && resolvedWidthLimit) {
                    const brokenWordLayout = buildLayout(layout.fontSize, true)
                    if (doesLayoutFit(brokenWordLayout)) {
                        layout = brokenWordLayout
                    }
                }
            }

            const strokeTotal =
                (effectiveOuter?.width ?? 0) + (effectiveInner?.width ?? 0)

            const absOffsetY = Math.abs(offsetY)
            const contentWidthPx = Math.max(
                layout.textWidth,
                resolvedWidthLimit ?? layout.textWidth,
            )
            let contentHeightPx = Math.max(
                layout.textHeight,
                resolvedHeightLimit ?? layout.textHeight,
            )
            if (baselineAligned && layout.lines.length > 0) {
                const firstLineDescent =
                    layout.lines[0]?.descent ?? layout.descent
                contentHeightPx = Math.max(
                    contentHeightPx,
                    layout.ascent + firstLineDescent,
                )
            }

            const canvasCssWidthPx = Math.ceil(
                contentWidthPx + padding * 2 + strokeTotal * 2,
            )
            const canvasCssHeightPx = Math.ceil(
                contentHeightPx + padding * 2 + strokeTotal * 2 + absOffsetY,
            )
            const wrapperWidthPx = Math.ceil(contentWidthPx)
            const wrapperHeightPx = Math.ceil(contentHeightPx)
            const toRem = (px: number) => `${px / 16}rem`

            // 适配高分屏
            canvas.width = canvasCssWidthPx * ratio
            canvas.height = canvasCssHeightPx * ratio
            ctx.setTransform(ratio, 0, 0, ratio, 0, 0)

            // 清空并设置绘图属性
            ctx.clearRect(0, 0, canvasCssWidthPx, canvasCssHeightPx)
            ctx.font = getFontString(layout.fontSize)
            ctx.lineJoin = "round"
            ctx.lineCap = "round"
            ctx.textBaseline = "alphabetic"
            ctx.textAlign = "left"

            // 计算绘制基点。baselineAligned 时首行基线固定为 layout.ascent，否则文本块与每行均垂直居中。
            const drawOffsetY = offsetY < 0 ? absOffsetY : 0
            const blockOffsetY = baselineAligned
                ? 0
                : (contentHeightPx - layout.textHeight) / 2

            // 填充字形
            if (fillPattern) {
                const patternCanvas = document.createElement("canvas")
                const size = fillPattern.width
                patternCanvas.width = size * 2
                patternCanvas.height = size * 2
                const pCtx = patternCanvas.getContext("2d")
                if (pCtx) {
                    pCtx.fillStyle = fillPattern.color1
                    pCtx.fillRect(0, 0, size, size)
                    pCtx.fillRect(size, size, size, size)
                    pCtx.fillStyle = fillPattern.color2
                    pCtx.fillRect(size, 0, size, size)
                    pCtx.fillRect(0, size, size, size)

                    const pattern = ctx.createPattern(patternCanvas, "repeat")
                    if (pattern) {
                        ctx.fillStyle = pattern
                    } else {
                        ctx.fillStyle = fillColor
                    }
                } else {
                    ctx.fillStyle = fillColor
                }
            } else {
                ctx.fillStyle = fillColor
            }

            layout.lines.forEach((line, index) => {
                const lineX =
                    textAlign === "left"
                        ? padding + strokeTotal
                        : textAlign === "right"
                          ? padding + strokeTotal + contentWidthPx - line.width
                          : padding +
                            strokeTotal +
                            (contentWidthPx - line.width) / 2
                const lineBoxTop =
                    padding +
                    strokeTotal +
                    blockOffsetY +
                    index * layout.lineHeight
                const lineY =
                    index === 0 && baselineAligned
                        ? lineBoxTop + layout.ascent + drawOffsetY
                        : (() => {
                              const lineVerticalOffset = Math.max(
                                  0,
                                  (layout.lineHeight -
                                      (line.ascent + line.descent)) /
                                      2,
                              )
                              return (
                                  lineBoxTop +
                                  lineVerticalOffset +
                                  line.ascent +
                                  drawOffsetY
                              )
                          })()

                // 双层描边（outer + inner）
                if (
                    effectiveOuter &&
                    effectiveOuter.width > 0 &&
                    effectiveInner &&
                    effectiveInner.width > 0
                ) {
                    // 外圈（更粗，留出内圈宽度）
                    ctx.strokeStyle = effectiveOuter.color
                    ctx.lineWidth =
                        2 * (effectiveOuter.width + effectiveInner.width)
                    ctx.strokeText(line.text, lineX, lineY + offsetY)

                    // 内圈（贴近字形轮廓）
                    ctx.strokeStyle = effectiveInner.color
                    ctx.lineWidth = 2 * effectiveInner.width
                    ctx.strokeText(line.text, lineX, lineY + offsetY)
                } else if (effectiveOuter && effectiveOuter.width > 0) {
                    // 单层外描边
                    ctx.strokeStyle = effectiveOuter.color
                    ctx.lineWidth = 2 * effectiveOuter.width
                    ctx.strokeText(line.text, lineX, lineY + offsetY)
                } else if (effectiveInner && effectiveInner.width > 0) {
                    // 仅有内圈设置时，也按单层描边处理
                    ctx.strokeStyle = effectiveInner.color
                    ctx.lineWidth = 2 * effectiveInner.width
                    ctx.strokeText(line.text, lineX, lineY + offsetY)
                }

                ctx.fillText(line.text, lineX, lineY)
            })

            // 设置外层容器尺寸（内容盒大小，单位 rem），并移动 canvas 保留描边和偏移缓冲区。
            const wrapper = wrapperRef.current
            if (wrapper) {
                wrapper.style.width = toRem(wrapperWidthPx)
                wrapper.style.height = toRem(wrapperHeightPx)
                wrapper.style.overflow = "visible"
            }
            canvas.style.position = "absolute"
            canvas.style.width = toRem(canvasCssWidthPx)
            canvas.style.height = toRem(canvasCssHeightPx)
            const offsetLeft = padding + strokeTotal
            // 如果内部绘制下移了 (offsetY < 0)，canvas 也要相应上移，保证文本视觉位置不变
            const offsetTop =
                padding + strokeTotal + (offsetY < 0 ? absOffsetY : 0)
            canvas.style.left = toRem(-offsetLeft)
            canvas.style.top = toRem(-offsetTop)
        }

        void draw()

        return () => {
            cancelled = true
        }
    }, [
        text,
        fillColor,
        fillPattern,
        fontFamily,
        fontSize,
        fontWeightValue,
        lineHeight,
        lineHeightRatio,
        padding,
        offsetY,
        maxWidth,
        maxHeight,
        maxFontSize,
        minFontSize,
        maxLines,
        textAlign,
        baselineAligned,
        balanceLines,
        dpr,
        effectiveOuter,
        effectiveInner,
    ])

    return (
        <div
            ref={wrapperRef}
            className={classNames(styles.wrapper, className)}
            style={style}
        >
            <canvas ref={canvasRef} aria-label={text} role="img" />
        </div>
    )
}

export default React.memo(StrokeText)
